import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useSnackbar } from "notistack"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { invalidate } from "lib/graphql/cache"
import {
    getMaintenanceCategory,
    getUnmappedMaintenanceDocuments,
    reprocessMaintenanceCategory,
} from "../../controller/maintenance-library"
import { UnmappedDocumentRow } from "./unmapped-document-row"

const EMPTY_LIST = []

export function UnmappedDocumentsList() {
    useEffect(refreshUnmappedDocuments, [])

    const unmappedDocuments = getUnmappedMaintenanceDocuments.useResults()
    const [hiddenDocumentIds, setHiddenDocumentIds] = useState({})
    const [processingDocumentIds, setProcessingDocumentIds] = useState({})
    const { enqueueSnackbar } = useSnackbar()
    const { readOnly } = useBoundContext()
    const documents = unmappedDocuments || EMPTY_LIST
    const visibleDocuments = useMemo(getVisibleDocuments, [documents, hiddenDocumentIds])
    const pendingDocuments = useMemo(getPendingDocuments, [visibleDocuments, processingDocumentIds])
    const processingCount = useMemo(getProcessingCount, [visibleDocuments, processingDocumentIds])
    const isLoading = unmappedDocuments === undefined
    const hasPendingDocuments = pendingDocuments.length > 0

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} spacing={2}>
                <Typography variant="subtitle1">{getTitle()}</Typography>
                <If value={!readOnly}>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleReprocessAll}
                        disabled={isLoading || !hasPendingDocuments}
                        startIcon={getReprocessAllIcon()}
                    >
                        <If value={processingCount > 0}>
                            Reprocess Remaining
                            <Else>Reprocess All</Else>
                        </If>
                    </Button>
                </If>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Documents with source text that currently have no extracted components.
            </Typography>

            <If value={unmappedDocuments === undefined}>
                <Typography variant="body2" color="text.secondary">
                    Loading unmapped documents...
                </Typography>

                <Else>
                    <If value={visibleDocuments.length > 0}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Document</TableCell>
                                    <TableCell>Path</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <Repeat
                                    list={visibleDocuments}
                                    item={
                                        <UnmappedDocumentRow
                                            onReprocess={reprocessDocument}
                                            processingById={processingDocumentIds}
                                        />
                                    }
                                />
                            </TableBody>
                        </Table>

                        <Else>
                            <Typography variant="body2" color="text.secondary">
                                No unmapped documents found.
                            </Typography>
                        </Else>
                    </If>
                </Else>
            </If>
        </Box>
    )

    function getTitle() {
        if (!visibleDocuments.length) return "Unmapped Documents"
        return `Unmapped Documents (${visibleDocuments.length})`
    }

    function getVisibleDocuments() {
        return documents.filter((document) => !hiddenDocumentIds[document.id] && (document.taskCount || 0) === 0)
    }

    function getPendingDocuments() {
        return visibleDocuments.filter((document) => !processingDocumentIds[document.id])
    }

    function getProcessingCount() {
        return visibleDocuments.filter((document) => processingDocumentIds[document.id]).length
    }

    async function handleReprocessAll() {
        if (!hasPendingDocuments) return
        await Promise.all(pendingDocuments.map(reprocessDocument))
    }

    async function reprocessDocument(document) {
        if (!document?.id) return
        if (processingDocumentIds[document.id]) return

        setDocumentProcessing(document.id, true)
        try {
            const sourceText = await loadSourceText(document)
            if (!sourceText.trim()) {
                throw new Error(`"${document.name}" has no source text to reprocess.`)
            }

            await reprocessMaintenanceCategory(document.id, sourceText)

            const updatedCategory = await getMaintenanceCategory(document.id)
            const hasTasks = (updatedCategory?.tasks || []).length > 0
            if (hasTasks) {
                markDocumentAsMapped(document.id)
                enqueueSnackbar(`Reprocessed "${document.name}"`, { variant: "success" })
                return
            }

            enqueueSnackbar(`Reprocessed "${document.name}" but no components were extracted`, { variant: "warning" })
        } catch (error) {
            enqueueSnackbar(getErrorMessage(error), { variant: "error" })
        } finally {
            setDocumentProcessing(document.id, false)
        }
    }

    async function loadSourceText(document) {
        if ((document.originalText || "").trim()) {
            return document.originalText
        }
        const category = await getMaintenanceCategory(document.id)
        return category?.originalText || ""
    }

    function setDocumentProcessing(documentId, isProcessing) {
        if (isProcessing) {
            setProcessingDocumentIds((current) => {
                if (current[documentId]) return current
                return { ...current, [documentId]: true }
            })
            return
        }

        setProcessingDocumentIds((current) => {
            if (!current[documentId]) return current
            const next = { ...current }
            delete next[documentId]
            return next
        })
    }

    function markDocumentAsMapped(documentId) {
        setHiddenDocumentIds((current) => {
            if (current[documentId]) return current
            return { ...current, [documentId]: true }
        })
    }

    function getReprocessAllIcon() {
        if (processingCount <= 0) return undefined
        return <CircularProgress size={14} color="inherit" />
    }

    function getErrorMessage(error) {
        const graphQlMessage = error?.graphQLErrors?.[0]?.message
        if (graphQlMessage) return graphQlMessage
        if (error?.message) return error.message
        return "Reprocessing failed."
    }

    function refreshUnmappedDocuments() {
        invalidate("getUnmappedMaintenanceDocuments")
    }
}
